/**
 * MM Bundle Builder
 * A portable, theme-agnostic bundle builder for Shopify.
 * Uses Alpine.js for reactivity with URL-based state management.
 */

(function() {
  'use strict';

  // ========================================
  // Global Initialization Tracker
  // ========================================
  
  // Use window-level tracking to persist across script reloads (e.g., from Barba.js page transitions)
  window._mmBundleState = window._mmBundleState || {
    initializedSections: new Set(),
    componentInstances: new Map(),
    registered: false
  };
  
  const initializedSections = window._mmBundleState.initializedSections;
  const componentInstances = window._mmBundleState.componentInstances;

  // ========================================
  // Pure Business Logic Functions (Testable)
  // ========================================

  /**
   * Calculate total quantity from items map
   * @param {Object} items - Map of variantId -> quantity
   * @returns {number} Total quantity
   */
  function calculateTotalQuantity(items) {
    return Object.values(items).reduce((sum, qty) => sum + qty, 0);
  }

  /**
   * Get effective minimum (max of URL original_min and schema min)
   * @param {number|null} originalMin - From URL params
   * @param {number} schemaMin - From section schema
   * @returns {number} Effective minimum
   */
  function getEffectiveMin(originalMin, schemaMin) {
    return Math.max(originalMin || 0, schemaMin || 1);
  }

  /**
   * Validate if quantity is within bounds
   * @param {number} total - Current total quantity
   * @param {number} effectiveMin - Effective minimum
   * @param {number} max - Maximum allowed
   * @returns {boolean} Whether quantity is valid
   */
  function validateQuantity(total, effectiveMin, max) {
    return total >= effectiveMin && total <= max;
  }

  /**
   * Get active tier based on effective minimum
   * @param {number} effectiveMin - Effective minimum quantity
   * @param {Array} tiers - Array of tier configurations
   * @returns {Object|null} Active tier or null
   */
  function getActiveTier(effectiveMin, tiers) {
    if (!tiers || tiers.length === 0) return null;
    
    return tiers
      .slice()
      .sort((a, b) => b.min_quantity - a.min_quantity)
      .find(tier => effectiveMin >= tier.min_quantity) || tiers[0];
  }

  /**
   * Get unlocked gifts based on total quantity
   * @param {number} totalQty - Current total quantity
   * @param {Array} giftConfigs - Array of gift configurations
   * @returns {Array} Array of unlocked gifts
   */
  function getUnlockedGifts(totalQty, giftConfigs) {
    if (!giftConfigs || giftConfigs.length === 0) return [];
    
    return giftConfigs.filter(gift => {
      if (gift.always_included) return true;
      return totalQty >= gift.unlock_quantity;
    });
  }

  /**
   * Calculate combined savings percentage from all sources
   * Uses multiplicative stacking: (1-a)(1-b)(1-c) = total multiplier
   * @param {number} tierSavings - Tier savings percentage (0-100)
   * @param {number} planSavings - Selling plan savings percentage (0-100)
   * @param {Array} discountConfigs - Array of discount savings configs
   * @param {string} purchaseType - 'one_time' or 'subscription'
   * @param {number} quantity - Current total quantity
   * @returns {number} Combined savings percentage (0-100)
   */
  function calculateCombinedSavings(tierSavings, planSavings, discountConfigs, purchaseType, quantity) {
    let multiplier = 1;
    
    // Apply tier savings
    if (tierSavings > 0) {
      multiplier *= (1 - tierSavings / 100);
    }
    
    // Apply selling plan savings
    if (planSavings > 0) {
      multiplier *= (1 - planSavings / 100);
    }
    
    // Apply matching discount savings blocks
    if (discountConfigs && discountConfigs.length > 0) {
      discountConfigs.forEach(config => {
        if (config.discount_percentage <= 0) return;
        if (quantity < config.min_quantity) return;
        
        const appliesTo = config.applies_to || 'both';
        const matches = appliesTo === 'both' || appliesTo === purchaseType;
        
        if (matches) {
          multiplier *= (1 - config.discount_percentage / 100);
        }
      });
    }
    
    return Math.round((1 - multiplier) * 100);
  }

  /**
   * Get CTA button text based on current state
   * @param {number} totalQty - Current total quantity
   * @param {number} effectiveMin - Effective minimum
   * @param {string} labelPlural - Plural label (e.g., "boxes")
   * @param {string} labelSingular - Singular label (e.g., "box")
   * @param {string} ctaValid - Valid state CTA text
   * @returns {string} CTA button text
   */
  function getCtaText(totalQty, effectiveMin, labelPlural, labelSingular, ctaValid) {
    const remaining = effectiveMin - totalQty;
    
    if (remaining <= 0) return ctaValid;
    // First selection should always say "select at least", even when min=1
    if (totalQty === 0) {
      const label = effectiveMin === 1 ? labelSingular : labelPlural;
      return `Select at least ${effectiveMin} ${label}`;
    }
    // Only show "select your last" if user has already selected something
    if (remaining === 1) return `Select your last ${labelSingular}`;
    return `Select ${remaining} more ${labelPlural}`;
  }

  /**
   * Filter selling plans based on visibility setting
   * @param {Array} allPlans - All available selling plans
   * @param {string} visibility - Visibility setting
   * @returns {Array} Filtered selling plans
   */
  function filterSellingPlans(allPlans, visibility) {
    if (visibility === 'one_time_only') return [];
    return allPlans || [];
  }

  /**
   * Get default selling plan based on tier, visibility, and default purchase type
   * @param {string|null} tierPlanId - Tier's selling plan ID
   * @param {string} visibility - Visibility setting
   * @param {Array} allPlans - All available plans
   * @param {string} defaultPurchaseType - Default purchase type ('one_time' or 'subscription')
   * @returns {string|null} Default selling plan ID
   */
  function getDefaultSellingPlan(tierPlanId, visibility, allPlans, defaultPurchaseType) {
    // If one-time only visibility, always return null
    if (visibility === 'one_time_only') return null;
    
    // If tier has a selling plan ID, use it
    if (tierPlanId) return String(tierPlanId);
    
    // If subscription only, or if default is subscription, select first available plan
    if ((visibility === 'subscription_only' || defaultPurchaseType === 'subscription') && allPlans && allPlans.length > 0) {
      return String(allPlans[0].id);
    }
    
    // Default to one-time (null)
    return null;
  }

  /**
   * Generate a unique bundle ID
   * @returns {string} Unique bundle ID
   */
  function generateBundleId() {
    return 'bundle_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Normalize selling plan ID to a consistent format for comparison
   * Handles both "gid://shopify/SellingPlan/123456" and "123456" formats
   * @param {string|number} id - Selling plan ID in any format
   * @returns {string} Normalized ID (numeric string)
   */
  function normalizeSellingPlanId(id) {
    if (!id) return '';
    const idStr = String(id);
    // Extract numeric ID from GID format if present
    const match = idStr.match(/SellingPlan\/(\d+)/);
    return match ? match[1] : idStr;
  }

  /**
   * Find a selling plan by ID, handling different ID formats
   * @param {Array} sellingPlans - Available selling plans
   * @param {string} planId - Plan ID to find (any format)
   * @returns {Object|undefined} Found plan or undefined
   */
  function findSellingPlan(sellingPlans, planId) {
    if (!sellingPlans || !planId) return undefined;
    const normalizedId = normalizeSellingPlanId(planId);
    return sellingPlans.find(p => normalizeSellingPlanId(p.id) === normalizedId);
  }

  /**
   * Calculate tier display price
   * @param {Object} tier - Tier configuration
   * @param {number} basePrice - Base price from product
   * @param {string|null} selectedPlanId - Currently selected selling plan ID
   * @param {Array} sellingPlans - Available selling plans
   * @returns {string} Formatted price string
   */
  function calculateTierPrice(tier, basePrice, selectedPlanId, sellingPlans) {
    // 1. Use manual display_price if set (but allow blank/whitespace to hide price)
    if (tier.display_price !== undefined && tier.display_price !== null) {
      const trimmed = String(tier.display_price).trim();
      if (trimmed === '') {
        return ''; // Return empty string to hide price
      }
      return trimmed;
    }

    // 2. Start with base price
    let price = basePrice;
    
    // 3. Apply tier savings percentage if set
    if (tier.savings_percent && tier.savings_percent > 0) {
      price = basePrice * (1 - tier.savings_percent / 100);
    }

    // 4. Apply selling plan discount if a plan is selected
    if (selectedPlanId && sellingPlans && sellingPlans.length > 0) {
      const selectedPlan = findSellingPlan(sellingPlans, selectedPlanId);
      if (selectedPlan && selectedPlan.price_adjustments && selectedPlan.price_adjustments.length > 0) {
        const adjustment = selectedPlan.price_adjustments[0];
        if (adjustment.value_type === 'percentage') {
          // Percentage discount (e.g., 10% off)
          price = price * (1 - adjustment.value / 100);
        } else if (adjustment.value_type === 'fixed_amount') {
          // Fixed amount discount (value is in cents)
          price = price - (adjustment.value / 100);
        } else if (adjustment.value_type === 'price') {
          // Fixed price override (value is the new price in cents)
          price = adjustment.value / 100;
        }
      }
    }
    
    // Ensure price doesn't go negative
    price = Math.max(0, price);
    
    // Don't show price if base price is 0
    if (price === 0 || basePrice === 0) {
      return '';
    }
    
    // Format as currency
    return '$' + price.toFixed(2) + '/ea';
  }

  /**
   * Calculate tier bar progress percentage
   * @param {number} totalQty - Current total quantity
   * @param {Array} tiers - Sorted array of tiers
   * @returns {number} Progress percentage (0-100)
   */
  function calculateTierProgress(totalQty, tiers) {
    if (!tiers || tiers.length === 0) return 0;
    
    const sortedTiers = tiers.slice().sort((a, b) => a.min_quantity - b.min_quantity);
    const maxTier = sortedTiers[sortedTiers.length - 1];
    
    if (totalQty >= maxTier.min_quantity) return 100;
    if (totalQty === 0) return 0;
    
    // Find current tier position
    let currentTierIndex = -1;
    for (let i = sortedTiers.length - 1; i >= 0; i--) {
      if (totalQty >= sortedTiers[i].min_quantity) {
        currentTierIndex = i;
        break;
      }
    }
    
    // Calculate progress
    const tierCount = sortedTiers.length;
    if (currentTierIndex === -1) {
      // Before first tier
      const firstTier = sortedTiers[0];
      const progressToFirst = (totalQty / firstTier.min_quantity) * (100 / tierCount);
      return Math.min(progressToFirst, 100 / tierCount * 0.5);
    }
    
    // At or past a tier
    const baseProgress = ((currentTierIndex + 1) / tierCount) * 100;
    
    // Add partial progress toward next tier
    if (currentTierIndex < tierCount - 1) {
      const currentTier = sortedTiers[currentTierIndex];
      const nextTier = sortedTiers[currentTierIndex + 1];
      const progressInSegment = (totalQty - currentTier.min_quantity) / (nextTier.min_quantity - currentTier.min_quantity);
      const segmentWidth = 100 / tierCount;
      return baseProgress + (progressInSegment * segmentWidth * 0.8); // 80% into next segment max
    }
    
    return baseProgress;
  }

  /**
   * Parse items from URL string
   * @param {string} itemsStr - Items string from URL (e.g., "123:2,456:1")
   * @returns {Object} Items map
   */
  function parseItemsFromUrl(itemsStr) {
    if (!itemsStr) return {};
    
    const items = {};
    itemsStr.split(',').forEach(pair => {
      const [id, qty] = pair.split(':');
      if (id && qty) {
        items[id] = parseInt(qty, 10) || 0;
      }
    });
    return items;
  }

  /**
   * Serialize items to URL string
   * @param {Object} items - Items map
   * @returns {string} URL-safe items string
   */
  function serializeItemsToUrl(items) {
    return Object.entries(items)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => `${id}:${qty}`)
      .join(',');
  }

  // ========================================
  // URL State Management
  // ========================================

  const urlState = {
    get(key) {
      const params = new URLSearchParams(window.location.search);
      return params.get(key);
    },

    set(key, value) {
      const params = new URLSearchParams(window.location.search);
      if (value === null || value === undefined || value === '') {
        params.delete(key);
      } else {
        params.set(key, value);
      }
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    },

    getAll() {
      const params = new URLSearchParams(window.location.search);
      return Object.fromEntries(params.entries());
    }
  };

  // ========================================
  // Alpine.js Component
  // ========================================

  function mmBundle(sectionId) {
    // Return cached instance if already created (prevents duplicate state)
    if (componentInstances.has(sectionId)) {
      console.log('MM Bundle: Returning cached instance for', sectionId);
      return componentInstances.get(sectionId);
    }
    
    const instance = {
      // State
      bundleId: '',
      items: {},
      originalMin: null,
      selectedSellingPlan: null,
      modalItem: null,
      frequencyDropdownOpen: false,
      isSubmitting: false,
      _wasBelowSubscriptionMin: true, // Track if we were below the subscription minimum
      config: {
        sectionId: '',
        enableRedesign: false,
        mode: 'variants',
        minQuantity: 1,
        maxQuantity: 10,
        quantityLabel: 'items',
        quantityLabelSingular: 'item',
        purchaseOptionsVisibility: 'both',
        defaultPurchaseType: 'subscription',
        ctaTextValid: 'Proceed to Checkout',
        tierBarEnabled: false,
        tierBarCheckImage: '',
        tiers: [],
        freeGifts: [],
        items: [],
        sellingPlans: [],
        checkoutBehavior: 'cart_drawer', // 'cart_page', 'cart_drawer', or 'checkout'
        reloadAfterAdd: false,
        cartDrawerDelay: 0,
        cartDrawerEvents: ['cart:update', 'cart:open', 'cart:updated', 'cart:refresh'],
        titleSplitDelimiter: '',
        titleSplitIndex: 0,
      },

      // Initialize
      init() {
        // Prevent duplicate initialization using global tracker
        if (initializedSections.has(sectionId)) {
          console.log('MM Bundle: Already initialized for section', sectionId, '- skipping');
          return;
        }
        
        // Also check DOM element for initialization marker
        const bundleEl = document.getElementById(`mm-bundle-${sectionId}`);
        if (bundleEl && bundleEl.hasAttribute('data-mm-initialized')) {
          console.log('MM Bundle: DOM element already initialized for section', sectionId, '- skipping');
          return;
        }
        
        // Mark as initialized
        initializedSections.add(sectionId);
        if (bundleEl) {
          bundleEl.setAttribute('data-mm-initialized', 'true');
        }
        
        console.log('MM Bundle: Initializing for section', sectionId);
        
        // Load config from hydration script
        const configScript = document.querySelector(`#mm-bundle-${sectionId} [data-mm-bundle-config]`);
        console.log('MM Bundle: Config script found:', !!configScript);
        
        if (configScript) {
          try {
            const rawText = configScript.textContent;
            console.log('MM Bundle: Raw config length:', rawText.length);
            const configData = JSON.parse(rawText);
            console.log('MM Bundle: Parsed config:', configData);
            console.log('MM Bundle: Items count:', configData.items?.length || 0);
            Object.assign(this.config, configData);
          } catch (e) {
            console.error('MM Bundle: Failed to parse config', e);
            console.error('MM Bundle: Config text:', configScript.textContent.substring(0, 500));
          }
        } else {
          console.warn('MM Bundle: No config script found for section', sectionId);
        }

        // Load state from URL
        this.bundleId = urlState.get('bundle_id') || generateBundleId();
        this.items = parseItemsFromUrl(urlState.get('items'));
        this.originalMin = parseInt(urlState.get('original_min'), 10) || null;
        const builderElement = document.querySelector(`#mm-bundle-${sectionId}`);
        this.checkoutBehavior = builderElement?.getAttribute('data-checkout-behavior') || 'cart_drawer';
        this.reloadAfterAdd = builderElement?.getAttribute('data-reload-after-add') === 'true';
        this.cartDrawerDelay = parseInt(builderElement?.getAttribute('data-cart-drawer-delay') || '0', 10);
        this.config.titleSplitDelimiter = builderElement?.getAttribute('data-title-split-delimiter') || '';
        this.config.titleSplitIndex = parseInt(builderElement?.getAttribute('data-title-split-index') || '0', 10);

        // Set default selling plan based on default tier (effectiveMin) and default purchase type
        const defaultTier = this.defaultTier;
        this.selectedSellingPlan = getDefaultSellingPlan(
          defaultTier?.selling_plan_id,
          this.config.purchaseOptionsVisibility,
          this.availableSellingPlans,
          this.config.defaultPurchaseType || 'subscription'
        );

        // Sync initial state to URL
        this.syncToUrl();

        // Watch for changes and sync to URL
        this.$watch('items', () => this.syncToUrl());
        
        // Watch for quantity changes to auto-switch selling plan if enabled
        if (this.config.autoSwitchSellingPlan) {
          let previousTierMin = this.activeTier?.min_quantity;
          
          this.$watch('totalQuantity', (newQty) => {
            const newTier = getActiveTier(newQty, this.filteredTiers);
            const newTierMin = newTier?.min_quantity;
            
            // Only switch if we've moved to a different tier
            if (newTierMin !== previousTierMin && newTier?.selling_plan_id) {
              this.selectedSellingPlan = newTier.selling_plan_id;
            }
            previousTierMin = newTierMin;
          });
        }
        
        console.log('MM Bundle: Initialization complete. Items:', this.config.items?.length || 0);
        
        // Check if we should open cart drawer (after page reload)
        this.checkAndOpenCartDrawer();

        // Initialize Swipers
        this.InitSwipers();
      },
      
      // Check for mm_open_cart param and open cart drawer if present
      checkAndOpenCartDrawer() {
        const url = new URL(window.location.href);
        if (url.searchParams.get('mm_open_cart') === '1') {
          console.log('MM Bundle: Opening cart drawer after page reload');
          
          // Remove the param from URL without reloading
          url.searchParams.delete('mm_open_cart');
          window.history.replaceState({}, '', url.toString());
          
          // Wait a moment for page to fully render, then open cart drawer
          setTimeout(() => {
            document.dispatchEvent(
              new CustomEvent('cart:drawer:refresh', { bubbles: true, detail: { open: true } })
            );
            const events = this.config.cartDrawerEvents || ['cart:update', 'cart:open', 'cart:updated', 'cart:refresh'];
            events.forEach(eventName => {
              if (eventName && eventName.trim()) {
                document.dispatchEvent(new CustomEvent(eventName.trim(), { bubbles: true }));
                console.log('MM Bundle: Dispatched event:', eventName.trim());
              }
            });
          }, 500);
        }
      },

      // Sync state to URL
      syncToUrl() {
        urlState.set('bundle_id', this.bundleId);
        urlState.set('items', serializeItemsToUrl(this.items));
        if (this.originalMin) {
          urlState.set('original_min', this.originalMin);
        }
      },

      // Computed: Effective minimum
      get effectiveMin() {
        return getEffectiveMin(this.originalMin, this.config.minQuantity);
      },

      // Computed: Total quantity
      get totalQuantity() {
        return calculateTotalQuantity(this.items);
      },

      // Computed: Remaining to minimum
      get remaining() {
        return Math.max(0, this.effectiveMin - this.totalQuantity);
      },

      // Computed: Is valid for checkout
      get isValid() {
        return validateQuantity(this.totalQuantity, this.effectiveMin, this.config.maxQuantity);
      },

      // Helper: Check if a tier applies to the current purchase type
      tierAppliesToCurrentType(tier) {
        if (!tier.applies_to || tier.applies_to === 'both') return true;
        return tier.applies_to === this.currentPurchaseType;
      },

      // Helper: Get tier type label (for purchase type display)
      getTierTypeLabel(tier) {
        const mode = this.config.tierMismatchDisplay;
        if (!mode || mode === 'hide' || mode === 'show_disabled') return '';
        
        // show_label_always: Always show label if tier has a specific applies_to
        if (mode === 'show_label_always') {
          if (tier.applies_to === 'subscription') return 'Subscription only';
          if (tier.applies_to === 'one_time') return 'One-time only';
          return '';
        }
        
        // show_label_mismatch: Show label only when mismatched
        if (mode === 'show_label_mismatch') {
          if (!this.tierAppliesToCurrentType(tier)) {
            if (tier.applies_to === 'subscription') return 'Subscription only';
            if (tier.applies_to === 'one_time') return 'One-time only';
          }
        }
        return '';
      },

      // Helper: Check if tier should show as disabled
      isTierDisabled(tier) {
        const mode = this.config.tierMismatchDisplay;
        // Disabled when mismatched AND in any show mode
        if (mode && mode.startsWith('show_')) {
          return !this.tierAppliesToCurrentType(tier);
        }
        return false;
      },

      // Helper: Get qualify badge text for mismatched tier
      getTierQualifyBadge(tier) {
        if (!this.isTierDisabled(tier)) return '';
        if (tier.applies_to === 'subscription') return this.config.qualifyBadgeSubscriptionText || 'Subscribe to qualify';
        if (tier.applies_to === 'one_time') return this.config.qualifyBadgeOnetimeText || 'One-time to qualify';
        return '';
      },

      // Computed: Filtered tiers based on current purchase type
      get filteredTiers() {
        const tiers = this.config.tiers || [];
        const mode = this.config.tierMismatchDisplay;
        // If any show mode, return all tiers
        if (mode && mode.startsWith('show_')) {
          return tiers;
        }
        // Otherwise filter to only matching tiers (hide mode)
        return tiers.filter(t => this.tierAppliesToCurrentType(t));
      },

      // Computed: Active tier (based on total quantity for display)
      get activeTier() {
        // Use totalQuantity for determining which tier applies to current selection
        // This makes savings update dynamically as user adds items
        return getActiveTier(this.totalQuantity, this.filteredTiers);
      },

      // Computed: Default tier (based on effectiveMin for initial selling plan selection)
      get defaultTier() {
        return getActiveTier(this.effectiveMin, this.filteredTiers);
      },

      // Computed: Savings percentage (tier savings + selling plan discount + discount savings)
      get savingsPercent() {
        const tierSavings = this.activeTier?.savings_percent || 0;
        const planSavings = this.selectedSellingPlan ? this.getSellingPlanDiscount() : 0;
        const purchaseType = this.selectedSellingPlan ? 'subscription' : 'one_time';
        
        return calculateCombinedSavings(
          tierSavings,
          planSavings,
          this.config.discountSavings || [],
          purchaseType,
          this.totalQuantity
        );
      },
      
      // Helper: Get selling plan discount percentage
      getSellingPlanDiscount() {
        if (!this.config.sellingPlans || this.config.sellingPlans.length === 0) return 0;
        const plan = findSellingPlan(this.config.sellingPlans, this.selectedSellingPlan);
        if (plan?.price_adjustments?.[0]?.value_type === 'percentage') {
          return plan.price_adjustments[0].value;
        }
        return 0;
      },

      // Computed: Sorted tiers for tier bar display (filtered by purchase type)
      get sortedTiers() {
        if (!this.filteredTiers || this.filteredTiers.length === 0) return [];
        return this.filteredTiers.slice().sort((a, b) => a.min_quantity - b.min_quantity);
      },

      // Computed: Tier progress percentage for progress bar
      get tierProgressPercent() {
        return calculateTierProgress(this.totalQuantity, this.filteredTiers);
      },

      // Computed: CTA text
      get ctaText() {
        return getCtaText(
          this.totalQuantity,
          this.effectiveMin,
          this.config.quantityLabel,
          this.config.quantityLabelSingular,
          this.config.ctaTextValid
        );
      },

      // Computed: Current purchase type for gift filtering
      get currentPurchaseType() {
        return this.selectedSellingPlan ? 'subscription' : 'one_time';
      },

      // Helper: Check if a gift applies to the current purchase type
      giftAppliesToCurrentType(gift) {
        if (!gift.applies_to || gift.applies_to === 'both') return true;
        return gift.applies_to === this.currentPurchaseType;
      },

      // Computed: Check if there's a mix of gift types (for showing labels)
      get hasMixedGiftTypes() {
        const gifts = this.config.freeGifts || [];
        const hasSubscriptionOnly = gifts.some(g => g.applies_to === 'subscription');
        const hasOneTimeOnly = gifts.some(g => g.applies_to === 'one_time');
        return hasSubscriptionOnly || hasOneTimeOnly;
      },

      // Computed: Filtered gifts based on current purchase type
      get filteredGifts() {
        const gifts = this.config.freeGifts || [];
        const mode = this.config.giftMismatchDisplay;
        // If any show mode, return all gifts
        if (mode && mode.startsWith('show_')) {
          return gifts;
        }
        // Otherwise filter to only matching gifts (hide mode)
        return gifts.filter(g => this.giftAppliesToCurrentType(g));
      },

      // Computed: Unlocked gifts (filtered by purchase type)
      get unlockedGifts() {
        return getUnlockedGifts(this.totalQuantity, this.filteredGifts);
      },

      // Computed: Unlocked gifts count
      get unlockedGiftsCount() {
        return this.unlockedGifts.filter(g => !g.always_included).length;
      },

      /* Computed: Total gifts count (excluding always-included) — the
       * denominator of "GIFTS UNLOCKED: x/y", so it counts the tiles on
       * screen (`displayedGifts`), not the eligible ones. The numerator above
       * stays eligibility-based: a mismatched tile is visible but uncounted,
       * which is exactly the "2/3 — Subscribe to qualify" state. Identical to
       * `filteredGifts` outside the redesign. */
      get totalGiftsCount() {
        return this.displayedGifts.filter(g => !g.always_included).length;
      },

      /* Computed: the gifts the tile row RENDERS — display only, never cart.
       *
       * The redesign always renders the full line-up so the Starter Kit reads
       * as a complete offer from 0 bags onward; tiles that are not earned yet
       * are dimmed rather than removed. `filteredGifts` drops purchase-type
       * mismatches outright, which silently deletes the subscription-only
       * tiles while autoship is locked below `minQuantityForSubscription`
       * (the row can't be on subscription yet, so it can never match).
       *
       * Eligibility is unchanged: `unlockedGifts` — what actually goes in the
       * cart — still reads `filteredGifts`, so rendering a tile never grants
       * it. `isGiftEarned()` keeps the two in sync visually. */
      get displayedGifts() {
        if (this.config.enableRedesign) return this.config.freeGifts || [];
        return this.filteredGifts;
      },

      // Computed: Quantity-based gifts (display list)
      get quantityBasedGifts() {
        return this.displayedGifts.filter(g => !g.always_included);
      },

      // Computed: Always-included gifts (display list)
      get alwaysIncludedGifts() {
        return this.displayedGifts.filter(g => g.always_included);
      },

      // Helper: Get gift type label (for purchase type display)
      getGiftTypeLabel(gift) {
        const mode = this.config.giftMismatchDisplay;
        if (!mode || mode === 'hide' || mode === 'show_disabled') return '';
        
        // show_label_always: Always show label if gift has a specific applies_to
        if (mode === 'show_label_always') {
          if (gift.applies_to === 'subscription') return 'Subscription only';
          if (gift.applies_to === 'one_time') return 'One-time only';
          return '';
        }
        
        // show_label_mismatch: Show label only when mismatched
        if (mode === 'show_label_mismatch') {
          if (!this.giftAppliesToCurrentType(gift)) {
            if (gift.applies_to === 'subscription') return 'Subscription only';
            if (gift.applies_to === 'one_time') return 'One-time only';
          }
        }
        return '';
      },

      // Helper: Check if gift should show as disabled
      isGiftDisabled(gift) {
        const mode = this.config.giftMismatchDisplay;
        // Disabled when mismatched AND in any show mode
        if (mode && mode.startsWith('show_')) {
          return !this.giftAppliesToCurrentType(gift);
        }
        return false;
      },

      // Helper: Get qualify badge text for mismatched gift
      getGiftQualifyBadge(gift) {
        if (!this.isGiftDisabled(gift)) return '';
        if (gift.applies_to === 'subscription') return this.config.qualifyBadgeSubscriptionText || 'Subscribe to qualify';
        if (gift.applies_to === 'one_time') return this.config.qualifyBadgeOnetimeText || 'One-time to qualify';
        return '';
      },

      // Computed: Show purchase options dropdown
      get showPurchaseOptions() {
        if (this.config.purchaseOptionsVisibility === 'one_time_only') return false;
        return this.availableSellingPlans.length > 0 || this.config.purchaseOptionsVisibility === 'both';
      },

      // Computed: Show subscription option (based on quantity)
      get showSubscriptionOption() {
        // Check if subscription visibility is allowed by config
        if (this.config.purchaseOptionsVisibility === 'one_time_only') return false;
        if (this.availableSellingPlans.length === 0) return false;
        
        // Check minimum quantity requirement
        const minQty = this.config.minQuantityForSubscription || 0;
        const isAboveMin = minQty === 0 || this.totalQuantity >= minQty;
        const isBelowMin = minQty > 0 && this.totalQuantity < minQty;
        
        if (isBelowMin) {
          // If subscription was selected but now hidden, switch to one-time
          if (this.selectedSellingPlan) {
            this.selectedSellingPlan = null;
          }
          // Track that we're below the threshold
          this._wasBelowSubscriptionMin = true;
          return false;
        }
        
        // Auto-switch TO subscription ONLY when crossing the threshold upward
        // (was below, now at/above) and default is subscription
        if (isAboveMin && this._wasBelowSubscriptionMin) {
          if (this.config.defaultPurchaseType === 'subscription') {
            // Use setTimeout to avoid modifying state during render
            setTimeout(() => {
              // Only switch if still at/above min and no plan selected
              if (!this.selectedSellingPlan && this.totalQuantity >= minQty) {
                this.selectFirstPlan();
              }
            }, 0);
          }
          // Mark that we've crossed the threshold (no longer "below")
          this._wasBelowSubscriptionMin = false;
        }
        
        return true;
      },

      // Computed: Available selling plans (from product's selling_plan_groups)
      get availableSellingPlans() {
        return filterSellingPlans(this.config.sellingPlans || [], this.config.purchaseOptionsVisibility);
      },

      // Computed: Selected selling plan name (for frequency selector)
      get selectedPlanName() {
        if (!this.selectedSellingPlan) return '';
        const plan = findSellingPlan(this.config.sellingPlans, this.selectedSellingPlan);
        return plan ? plan.name : '';
      },

      // Computed: Subscription savings percentage (combined tier + plan + discount savings)
      get subscriptionSavingsPercent() {
        const planSavings = this.getSubscriptionBaseSavings();
        const tierSavings = this.activeTier?.savings_percent || 0;
        
        return calculateCombinedSavings(
          tierSavings,
          planSavings,
          this.config.discountSavings || [],
          'subscription',
          this.totalQuantity
        );
      },
      
      // Helper: Get base subscription savings (from plan only, without stacking)
      getSubscriptionBaseSavings() {
        const plans = this.availableSellingPlans;
        if (!plans || plans.length === 0) return 0;
        
        // Get the selected plan, or fall back to first plan
        let targetPlan = null;
        if (this.selectedSellingPlan) {
          targetPlan = findSellingPlan(this.config.sellingPlans, this.selectedSellingPlan);
        }
        if (!targetPlan) {
          targetPlan = plans[0];
        }
        
        if (targetPlan && targetPlan.price_adjustments && targetPlan.price_adjustments.length > 0) {
          const adjustment = targetPlan.price_adjustments[0];
          if (adjustment.value_type === 'percentage') {
            return adjustment.value;
          }
        }
        return 0;
      },

      // Computed: Original price (before any discount) - sum of item prices
      get ctaOriginalPrice() {
        let total = 0;
        Object.entries(this.items).forEach(([variantId, qty]) => {
          const item = this.config.items.find(i => String(i.id) === String(variantId));
          if (item) total += item.price * qty;
        });
        return total; // In cents
      },

      // Computed: Discounted price for CTA (uses current savingsPercent which handles both purchase types)
      get ctaDiscountedPrice() {
        const original = this.ctaOriginalPrice;
        const savingsPercent = this.savingsPercent;
        return Math.round(original * (1 - savingsPercent / 100));
      },

      // Computed: Subscription original price (before discount)
      get subscriptionOriginalPrice() {
        return this.ctaOriginalPrice; // Same calculation
      },

      // Computed: Subscription discounted price (after all savings)
      get subscriptionDiscountedPrice() {
        const original = this.subscriptionOriginalPrice;
        const savingsPercent = this.subscriptionSavingsPercent;
        return Math.round(original * (1 - savingsPercent / 100));
      },

      // Computed: Per-item price for subscription
      get subscriptionPerItemPrice() {
        // Check if any item has items_per_unit metafield
        let totalItems = 0;
        let hasMetafield = false;
        
        Object.entries(this.items).forEach(([variantId, qty]) => {
          const item = this.config.items.find(i => String(i.id) === String(variantId));
          if (item && item.items_per_unit > 0) {
            hasMetafield = true;
            totalItems += item.items_per_unit * qty;
          }
        });
        
        // If no metafield, use total quantity
        if (!hasMetafield) {
          totalItems = this.totalQuantity;
        }
        
        if (totalItems === 0) return 0;
        return Math.round(this.subscriptionDiscountedPrice / totalItems);
      },

      // Computed: One-time original price (same as subscription - sum of item prices)
      get oneTimeOriginalPrice() {
        return this.ctaOriginalPrice;
      },

      // Computed: One-time discounted price (uses one-time savings - tier + discount blocks for one_time)
      get oneTimeDiscountedPrice() {
        const original = this.oneTimeOriginalPrice;
        const tierSavings = this.activeTier?.savings_percent || 0;
        
        // Calculate one-time savings (tier + discount blocks that apply to one_time)
        const oneTimeSavings = calculateCombinedSavings(
          tierSavings,
          0, // No selling plan discount for one-time
          this.config.discountSavings || [],
          'one_time',
          this.totalQuantity
        );
        
        return Math.round(original * (1 - oneTimeSavings / 100));
      },

      // Computed: Per-item price for one-time
      get oneTimePerItemPrice() {
        // Check if any item has items_per_unit metafield
        let totalItems = 0;
        let hasMetafield = false;
        
        Object.entries(this.items).forEach(([variantId, qty]) => {
          const item = this.config.items.find(i => String(i.id) === String(variantId));
          if (item && item.items_per_unit > 0) {
            hasMetafield = true;
            totalItems += item.items_per_unit * qty;
          }
        });
        
        // If no metafield, use total quantity
        if (!hasMetafield) {
          totalItems = this.totalQuantity;
        }
        
        if (totalItems === 0) return 0;
        return Math.round(this.oneTimeDiscountedPrice / totalItems);
      },

      // Computed: Check if all items in config have the same base price (variant mode only)
      get allVariantsSamePrice() {
        if (this.config.mode !== 'variants') return false;
        if (!this.config.items || this.config.items.length < 2) return true;
        const firstPrice = this.config.items[0].price;
        return this.config.items.every(item => item.price === firstPrice);
      },

      // Computed: Original single variant price (no discount)
      get singleItemOriginalPrice() {
        if (!this.config.items || this.config.items.length === 0) return 0;
        return this.config.items[0].price;
      },

      // Computed: Single variant price for one-time (base price with one-time discount applied)
      get oneTimeSingleItemPrice() {
        if (!this.config.items || this.config.items.length === 0) return 0;
        const basePrice = this.config.items[0].price;
        const tierSavings = this.activeTier?.savings_percent || 0;
        // For one-time, planSavings is 0 (no selling plan discount)
        const oneTimeSavings = calculateCombinedSavings(tierSavings, 0, this.config.discountSavings, 'one_time', this.totalQuantity);
        return Math.round(basePrice * (1 - oneTimeSavings / 100));
      },

      // Computed: Single variant price for subscription (base price with subscription discount applied)
      get subscriptionSingleItemPrice() {
        if (!this.config.items || this.config.items.length === 0) return 0;
        const basePrice = this.config.items[0].price;
        return Math.round(basePrice * (1 - this.subscriptionSavingsPercent / 100));
      },

      // Helper: Format money (cents to dollars)
      formatMoney(cents) {
        if (cents === 0 || cents === null || cents === undefined) return '$0.00';
        return '$' + (cents / 100).toFixed(2);
      },

      // Helper: Format title (split at delimiter if configured)
      formatTitle(title) {
        if (!title) return '';
        const delimiter = this.config.titleSplitDelimiter;
        if (!delimiter) return title;
        const parts = title.split(delimiter);
        const index = this.config.titleSplitIndex;
        if (index >= 0 && index < parts.length) {
          return parts[index].trim();
        }
        return title;
      },

      // Methods: Purchase options
      toggleFrequencyDropdown() {
        this.frequencyDropdownOpen = !this.frequencyDropdownOpen;
      },

      closeFrequencyDropdown() {
        this.frequencyDropdownOpen = false;
      },

      selectFirstPlan() {
        if (this.availableSellingPlans.length > 0) {
          this.selectedSellingPlan = String(this.availableSellingPlans[0].id);
        }
      },

      selectPlan(planId) {
        this.selectedSellingPlan = String(planId);
        this.frequencyDropdownOpen = false;
      },

      // Methods: Item management
      getItemQuantity(itemId) {
        return this.items[itemId] || 0;
      },

      // Debounce helper to prevent duplicate clicks from multiple Alpine event handlers
      _lastClickTime: 0,
      _isDebounced() {
        const now = Date.now();
        if (now - this._lastClickTime < 50) {
          return true; // Ignore clicks within 50ms of each other
        }
        this._lastClickTime = now;
        return false;
      },

      addItem(itemId) {
        if (this._isDebounced()) return;
        if (this.totalQuantity >= this.config.maxQuantity) return;

        this.items = {
          ...this.items,
          [itemId]: (this.items[itemId] || 0) + 1
        };
        this.updateHeroVariantImage(itemId);
      },

      incrementItem(itemId) {
        if (this._isDebounced()) return;
        if (this.totalQuantity >= this.config.maxQuantity) return;

        this.items = {
          ...this.items,
          [itemId]: (this.items[itemId] || 0) + 1
        };
        this.updateHeroVariantImage(itemId);
      },

      decrementItem(itemId) {
        if (this._isDebounced()) return;
        const currentQty = this.items[itemId] || 0;
        if (currentQty <= 0) return;

        const newItems = { ...this.items };
        if (currentQty === 1) {
          delete newItems[itemId];
          // Show the last remaining item's image, or revert to first item
          const remainingIds = Object.keys(newItems).filter(id => newItems[id] > 0);
          if (remainingIds.length > 0) {
            this.updateHeroVariantImage(Number(remainingIds[remainingIds.length - 1]));
          } else if (this.config.items.length > 0) {
            this.updateHeroVariantImage(this.config.items[0].id);
          }
        } else {
          newItems[itemId] = currentQty - 1;
        }
        this.items = newItems;
      },

      // Methods: Hero variant image — swaps the first slide's src to the selected flavor
      updateHeroVariantImage(itemId) {
        const item = this.config.items.find(i => i.id === itemId);
        if (!item) return;

        const heroSrc = item.hero_image || item.modal_image || item.image?.replace('width=200', 'width=800');
        if (!heroSrc) return;

        // Use section ID to find container in live DOM (this.$el may be stale after Barba transitions)
        const sectionEl = document.getElementById('mm-bundle-' + this.config.sectionId);
        const container = sectionEl ? sectionEl.querySelector('.mm-bundle__hero-inner') : null;
        if (!container) return;

        // Static main image is set in the theme editor — leave the hero alone
        if (container.dataset.mmHeroStatic === 'true') return;

        // Swap main hero image src
        const heroImg = container.querySelector('.mm-bundle__hero-variant-image');
        if (heroImg) {
          heroImg.src = heroSrc;
          heroImg.alt = item.title;
        }

        // Swap thumbnail src
        const thumbImg = container.querySelector('.mm-bundle__hero-variant-thumb img');
        if (thumbImg) {
          thumbImg.src = item.image;
          thumbImg.alt = item.title;
        }

        // Slide back to position 0 (the variant slide)
        const mainSwiper = container.querySelector('[data-mm-hero-main]');
        if (mainSwiper && mainSwiper.swiper) {
          const sw = mainSwiper.swiper;
          if (sw.params.loop) {
            sw.slideToLoop(0, 300);
          } else {
            sw.slideTo(0, 300);
          }
        }
        const thumbsSwiper = container.querySelector('[data-mm-hero-thumbs]');
        if (thumbsSwiper && thumbsSwiper.swiper) {
          thumbsSwiper.swiper.slideTo(0, 300);
        }
      },

      // Methods: Gift checking
      isGiftUnlocked(gift) {
        if (gift.always_included) return true;
        return this.totalQuantity >= gift.unlock_quantity;
      },

      /* Methods: is this tile actually earned right now? (redesign only)
       *
       * `isGiftUnlocked` is quantity-only. Since the redesign also renders
       * gifts that don't apply to the current purchase type, a
       * subscription-only gift past its quantity threshold would otherwise
       * show the green "unlocked" check on a One-Time order and then never
       * arrive — `unlockedGifts` (cart) filters it out. Mismatched gifts stay
       * in the locked state, labelled by `giftRemainingLabel` below. */
      isGiftEarned(gift) {
        return this.isGiftUnlocked(gift) && this.giftAppliesToCurrentType(gift);
      },

      /* Methods: sub-label for a tile that is not earned yet (redesign only).
       * Quantity comes first — "3 more bags" is the actionable step while
       * autoship is still locked. Once the quantity is there, an unearned
       * tile can only be a purchase-type mismatch, so name that instead. */
      giftRemainingLabel(gift) {
        if (this.isGiftUnlocked(gift) && !this.giftAppliesToCurrentType(gift)) {
          if (gift.applies_to === 'subscription') {
            return this.config.qualifyBadgeSubscriptionText || 'Subscribe to qualify';
          }
          if (gift.applies_to === 'one_time') {
            return this.config.qualifyBadgeOnetimeText || 'One-time to qualify';
          }
        }
        const remaining = Math.max(0, (gift.unlock_quantity || 0) - this.totalQuantity);
        const label = remaining === 1 ? this.config.quantityLabelSingular : this.config.quantityLabel;
        return `${remaining} more ${String(label || '').toLowerCase()}`;
      },

      /* ----------------------------------------------------------------
       * Redesign-only subscription lock state.
       *
       * The legacy `showSubscriptionOption` getter HIDES the subscription
       * row below the minimum quantity (and mutates selectedSellingPlan as
       * a side effect). The redesign instead SHOWS the row in a locked
       * state, so these getters are read-only and the invariant
       * (no selling plan while locked) is enforced by an x-effect in
       * snippets/mm-bundle-purchase-redesign.liquid.
       * ---------------------------------------------------------------- */
      get subscriptionMinQty() {
        return this.config.minQuantityForSubscription || 0;
      },

      get subscriptionLocked() {
        const min = this.subscriptionMinQty;
        return min > 0 && this.totalQuantity < min;
      },

      get subscriptionRemainingQty() {
        return Math.max(0, this.subscriptionMinQty - this.totalQuantity);
      },

      // "+ ADD 2 MORE BAGS TO UNLOCK — SAVE 40%"
      get subscriptionUnlockLabel() {
        const n = this.subscriptionRemainingQty;
        const label = n === 1 ? this.config.quantityLabelSingular : this.config.quantityLabel;
        const percent = this.subscriptionSavingsPercent;
        const base = `+ Add ${n} more ${String(label || '').toLowerCase()} to unlock`;
        return percent > 0 ? `${base} — save ${percent}%` : base;
      },

      // "Subscription requires 2+ bags — any mix of flavors."
      get subscriptionRequirementLabel() {
        const min = this.subscriptionMinQty;
        const label = min === 1 ? this.config.quantityLabelSingular : this.config.quantityLabel;
        return `Subscription requires ${min}+ ${String(label || '').toLowerCase()}`;
      },

      // Methods: Get gift inline styles (for per-gift customization)
      getGiftStyles(gift) {
        const styles = [];
        if (gift.bg_color) {
          // Set CSS variable so it only applies to image wrapper, not entire card
          styles.push(`--mm-gift-bg: ${gift.bg_color}`);
        }
        // Per-gift border settings
        if (gift.border_type && gift.border_type !== 'none') {
          styles.push(`--mm-gift-border-type: ${gift.border_type}`);
          styles.push(`--mm-gift-border-width: ${gift.border_width || 1}px`);
          if (gift.border_color) {
            styles.push(`--mm-gift-border-color: ${gift.border_color}`);
          }
        }
        if (gift.border_radius) {
          styles.push(`--mm-gift-border-radius: ${gift.border_radius}px`);
        }
        return styles.join('; ');
      },

      // Methods: Tier bar
      getTierPosition(index) {
        const tierCount = this.sortedTiers.length;
        if (tierCount <= 1) return 50;
        // Evenly space tiers with padding on edges
        const padding = 10; // 10% padding on each side
        const usableWidth = 100 - (padding * 2);
        return padding + (index / (tierCount - 1)) * usableWidth;
      },

      isTierActive(tier) {
        // Tier is active if we've reached it but haven't passed to the next higher tier
        const sorted = this.sortedTiers;
        const tierIndex = sorted.findIndex(t => t.min_quantity === tier.min_quantity);
        const nextTier = sorted[tierIndex + 1];
        
        if (this.totalQuantity < tier.min_quantity) return false;
        if (!nextTier) return this.totalQuantity >= tier.min_quantity;
        return this.totalQuantity >= tier.min_quantity && this.totalQuantity < nextTier.min_quantity;
      },

      getTierDisplayPrice(tier) {
        return calculateTierPrice(
          tier,
          this.config.basePrice || 0,
          this.selectedSellingPlan,
          this.availableSellingPlans
        );
      },

      // Methods: Modal
      openInfoModal(item) {
        this.modalItem = item;
        this.$refs.infoModal?.showModal();
      },

      closeInfoModal() {
        this.$refs.infoModal?.close();
        this.modalItem = null;
      },

      // Methods: Form submission
      async submitBundle() {
        if (!this.isValid || this.isSubmitting) return;

        this.isSubmitting = true;

        const formInputs = this.$refs.formInputs;
        if (!formInputs) {
          this.isSubmitting = false;
          return;
        }

        // Build items array for JSON submission (better for selling plans)
        const items = [];

        // Add selected items with selling plan (subscriptions only apply to bundle items, not gifts)
        Object.entries(this.items).forEach(([variantId, quantity]) => {
          if (quantity <= 0) return;

          const item = {
            id: parseInt(variantId, 10),
            quantity: quantity,
            properties: {
              _custom_bundle_id: this.bundleId,
              _bundle_item: 'true'
            }
          };

          // Add selling plan to bundle items (not gifts)
          if (this.selectedSellingPlan) {
            item.selling_plan = parseInt(this.selectedSellingPlan, 10);
          }

          items.push(item);
        });

        // Get current cart to check for existing gifts
        let currentCart = null;
        try {
          const cartResponse = await fetch('/cart.js', {
            headers: { 'Accept': 'application/json' }
          });
          currentCart = await cartResponse.json();
        } catch (e) {
          console.log('MM Bundle: Could not fetch cart, will add all gifts');
        }

        // Add unlocked free gifts (NO selling plan - gifts are one-time)
        this.unlockedGifts.forEach(gift => {
          if (!gift.variant_id) return; // Skip virtual gifts

          // Check if gift should be skipped (already in cart and allow_multiple is false)
          if (!gift.allow_multiple && currentCart) {
            const giftInCart = currentCart.items.some(item => item.variant_id === gift.variant_id);
            if (giftInCart) {
              console.log('MM Bundle: Skipping gift (already in cart):', gift.title);
              return;
            }
          }

          items.push({
            id: gift.variant_id,
            quantity: 1,
            properties: {
              _custom_bundle_id: this.bundleId,
              _bundle_gift: 'true'
            }
            // No selling_plan for gifts - they're always one-time
          });
        });

        // Debug: Log the payload
        console.log('MM Bundle: Submitting items:', JSON.stringify(items, null, 2));
        console.log('MM Bundle: Selected selling plan:', this.selectedSellingPlan);

        // Handle checkout behavior
        if (this.checkoutBehavior === 'cart_drawer') {
          // Cart Drawer: Add items via API, then open drawer
          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({ items })
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error('MM Bundle: Cart add error:', errorData);
              throw new Error(errorData.description || 'Failed to add items to cart');
            }

            const cartData = await response.json();
            console.log('MM Bundle: Items added to cart:', cartData);

            // If reload mode is enabled, reload the page with a param to open cart drawer
            if (this.reloadAfterAdd) {
              console.log('MM Bundle: Reloading page to open cart drawer');
              const url = new URL(window.location.href);
              url.searchParams.set('mm_open_cart', '1');
              window.location.href = url.toString();
              return;
            }

            let freshCart;
            if (typeof window._cart?.get === 'function') {
              freshCart = await window._cart.get();
            } else {
              const cartResponse = await fetch('/cart.js', {
                headers: { 'Accept': 'application/json' }
              });
              freshCart = await cartResponse.json();
            }
            console.log('MM Bundle: Fresh cart data:', freshCart);

            // Try to refresh cart drawer section via Shopify's section rendering API
            try {
              const sectionResponse = await fetch('/?sections=cart-drawer,cart-icon,cart-count');
              if (sectionResponse.ok) {
                const sections = await sectionResponse.json();
                console.log('MM Bundle: Cart sections refreshed');
                
                Object.entries(sections).forEach(([sectionId, html]) => {
                  const sectionEl = document.getElementById(`shopify-section-${sectionId}`);
                  if (sectionEl) {
                    sectionEl.innerHTML = html;
                    if (typeof Alpine !== 'undefined' && typeof Alpine.initTree === 'function') {
                      Alpine.initTree(sectionEl);
                    }
                  }
                });
              }
            } catch (sectionError) {
              console.log('MM Bundle: Section refresh not available, using events');
            }

            // Fire all configured cart drawer events with fresh cart data
            const events = this.config.cartDrawerEvents || ['cart:update', 'cart:open', 'cart:updated', 'cart:refresh'];
            const eventDetail = {
              bubbles: true,
              detail: {
                cart: freshCart,
                resource: freshCart,
                sourceId: this.config.sectionId,
                data: {
                  source: 'mm-bundle-builder',
                  itemCount: freshCart.item_count
                }
              }
            };

            events.forEach(eventName => {
              if (eventName && eventName.trim()) {
                const customEvent = new CustomEvent(eventName.trim(), eventDetail);
                document.dispatchEvent(customEvent);
                console.log('MM Bundle: Dispatched event:', eventName.trim());
              }
            });

            if (this.cartDrawerDelay > 0) {
              await new Promise(resolve => setTimeout(resolve, this.cartDrawerDelay));
            }

            if (typeof Alpine !== 'undefined' && typeof Alpine.nextTick === 'function') {
              await new Promise((resolve) => Alpine.nextTick(resolve));
            }

            document.dispatchEvent(new CustomEvent('cart:drawer:open', { bubbles: true }));

            // Reset submitting state after cart drawer is triggered
            this.isSubmitting = false;
          } catch (error) {
            console.error('MM Bundle: Error adding to cart:', error);
            alert('Failed to add items to cart. Please try again.');
            this.isSubmitting = false;
          }
        } else if (this.checkoutBehavior === 'checkout') {
          // Direct to Checkout: Add items via API, then redirect to /checkout
          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({ items })
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error('MM Bundle: Cart add error:', errorData);
              throw new Error(errorData.description || 'Failed to add items to cart');
            }

            console.log('MM Bundle: Items added, redirecting to checkout');
            window.location.href = '/checkout';
          } catch (error) {
            console.error('MM Bundle: Error adding to cart:', error);
            alert('Failed to add items to cart. Please try again.');
            this.isSubmitting = false;
          }
        } else {
          // Cart Page (default): Add items via API, then redirect to /cart
          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({ items })
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error('MM Bundle: Cart add error:', errorData);
              throw new Error(errorData.description || 'Failed to add items to cart');
            }

            console.log('MM Bundle: Items added, redirecting to cart');
            window.location.href = '/cart';
          } catch (error) {
            console.error('MM Bundle: Error adding to cart:', error);
            alert('Failed to add items to cart. Please try again.');
            this.isSubmitting = false;
          }
        }
      },

      InitSwipers() {
        // Skip if Swiper is not available
        if (typeof Swiper === 'undefined') return;
        
        // Find hero containers within the section
        let sectionContainer = this.$el;
        const heroContainers = sectionContainer.querySelectorAll('.mm-bundle__hero-inner');
        
        heroContainers.forEach(function(container) {
          const mainSwiper = container.querySelector('[data-mm-hero-main]');
          const thumbsSwiper = container.querySelector('[data-mm-hero-thumbs]');
          const paginationEl = container.querySelector('.mm-bundle__hero-pagination');
          
          if (!mainSwiper) return;
          
          // Skip if already initialized
          if (mainSwiper.swiper) return;
          
          // Initialize thumbs swiper first if it exists
          let thumbsSwiperInstance = null;
          if (thumbsSwiper && !thumbsSwiper.swiper) {
            thumbsSwiperInstance = new Swiper(thumbsSwiper, {
              spaceBetween: 10,
              slidesPerView: 'auto',
              freeMode: true,
              watchSlidesProgress: true,
              grabCursor: true,
            });
          }
          
          // Initialize main swiper
          const autoplayEnabled = mainSwiper.dataset.autoplay === 'true';
          const autoplayDelay = parseInt(mainSwiper.dataset.autoplayDelay || '5', 10) * 1000;
          const showArrows = mainSwiper.dataset.showArrows === 'true';
          const paginationType = mainSwiper.dataset.paginationType || 'none';
          const loopEnabled = mainSwiper.dataset.loop === 'true';
          const tapToZoom = mainSwiper.dataset.tapToZoom === 'true';
          const scrollbarEl = container.querySelector('.mm-bundle__hero-scrollbar');
          const progressBarEl = container.querySelector('.mm-bundle__hero-progress-bar');

          const isMobile = window.matchMedia('(max-width: 999px)').matches;

          const swiperConfig = {
            loop: loopEnabled,
            grabCursor: true,
            keyboard: {
              enabled: true,
            },
            ...(isMobile ? {
              touchEventsTarget: 'wrapper',
            } : {
              mousewheel: {
                forceToAxis: true,
              },
            }),
            pagination: paginationEl ? {
              el: paginationEl,
              clickable: true,
            } : false,
            thumbs: thumbsSwiperInstance ? {
              swiper: thumbsSwiperInstance,
            } : undefined,
            autoplay: autoplayEnabled ? {
              delay: autoplayDelay,
              disableOnInteraction: true,
            } : false,
          };
          
          // Add navigation arrows if enabled
          if (showArrows) {
            swiperConfig.navigation = {
              nextEl: container.querySelector('.swiper-button-next'),
              prevEl: container.querySelector('.swiper-button-prev'),
            };
          }
          
          // Add scrollbar if enabled
          if (paginationType === 'scrollbar' && scrollbarEl) {
            swiperConfig.scrollbar = {
              el: scrollbarEl,
              draggable: true,
            };
          }
          
          const swiperInstance = new Swiper(mainSwiper, swiperConfig);
          
          // Handle progress bar animation
          if (paginationType === 'progress' && progressBarEl && swiperInstance) {
            function updateProgressBar() {
              const progress = (swiperInstance.progress * 100).toFixed(2);
              progressBarEl.style.width = progress + '%';
            }
            
            // Update on slide change
            swiperInstance.on('slideChange', updateProgressBar);
            swiperInstance.on('progress', updateProgressBar);
            
            // Initial update
            updateProgressBar();
          }

          // Tap-to-zoom lightbox (mobile only)
          if (tapToZoom) {
            const sectionWrapper = mainSwiper.closest('.mm-bundlekit');
            const sectionId = sectionWrapper ? sectionWrapper.id.replace('mm-bundle-', '') : '';
            const lightbox = document.getElementById('mm-lightbox-' + sectionId);
            const lightboxImg = lightbox ? lightbox.querySelector('.mm-bundle__lightbox-img') : null;

            if (lightbox && lightboxImg) {
              let currentScale = 1;
              let panX = 0, panY = 0;
              const MIN_SCALE = 1;
              const MAX_SCALE = 4;
              let imageList = [];
              let currentIndex = 0;

              function applyTransform() {
                if (currentScale > 1) {
                  lightboxImg.style.transform = 'scale(' + currentScale + ') translate(' + panX + 'px, ' + panY + 'px)';
                  lightboxImg.classList.add('is-zoomed');
                } else {
                  lightboxImg.style.transform = '';
                  lightboxImg.classList.remove('is-zoomed');
                }
              }

              function setScale(val) {
                currentScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, val));
                if (currentScale <= 1) { panX = 0; panY = 0; }
                applyTransform();
              }

              function openLightbox(src, alt) {
                lightboxImg.src = src;
                lightboxImg.alt = alt || '';
                panX = 0; panY = 0;
                setScale(1);
                lightbox.classList.add('is-open');
                document.body.style.overflow = 'hidden';
              }

              function closeLightbox() {
                lightbox.classList.remove('is-open');
                document.body.style.overflow = '';
                panX = 0; panY = 0;
                setScale(1);
              }

              const arrowPrev = lightbox.querySelector('.mm-bundle__lightbox-arrow--prev');
              const arrowNext = lightbox.querySelector('.mm-bundle__lightbox-arrow--next');

              function updateArrows() {
                if (!arrowPrev || !arrowNext) return;
                arrowPrev.hidden = imageList.length <= 1;
                arrowNext.hidden = imageList.length <= 1;
              }

              function goToIndex(idx) {
                currentIndex = (idx + imageList.length) % imageList.length;
                panX = 0; panY = 0;
                setScale(1);
                lightboxImg.src = imageList[currentIndex].src;
                lightboxImg.alt = imageList[currentIndex].alt;
              }

              if (arrowPrev) arrowPrev.addEventListener('click', function(e) { e.stopPropagation(); goToIndex(currentIndex - 1); });
              if (arrowNext) arrowNext.addEventListener('click', function(e) { e.stopPropagation(); goToIndex(currentIndex + 1); });

              lightbox.querySelector('.mm-bundle__lightbox-close').addEventListener('click', closeLightbox);
              lightbox.addEventListener('click', function(e) { if (e.target === lightbox) closeLightbox(); });
              lightboxImg.addEventListener('click', function() { setScale(currentScale > 1 ? 1 : 2); });

              // Single-finger: pan when zoomed, swipe to navigate when not zoomed
              let touchStartX = 0, touchStartY = 0, isPanning = false;
              lightbox.addEventListener('touchstart', function(e) {
                if (e.touches.length === 1) {
                  touchStartX = e.touches[0].clientX;
                  touchStartY = e.touches[0].clientY;
                  isPanning = currentScale > 1;
                }
              }, { passive: true });

              lightbox.addEventListener('touchmove', function(e) {
                if (e.touches.length !== 1 || !isPanning) return;
                const dx = (e.touches[0].clientX - touchStartX) / currentScale;
                const dy = (e.touches[0].clientY - touchStartY) / currentScale;
                touchStartX = e.touches[0].clientX;
                touchStartY = e.touches[0].clientY;
                panX += dx;
                panY += dy;
                applyTransform();
              }, { passive: true });

              lightbox.addEventListener('touchend', function(e) {
                if (isPanning || e.changedTouches.length !== 1) { isPanning = false; return; }
                if (currentScale > 1) return;
                const dx = e.changedTouches[0].clientX - touchStartX;
                const dy = e.changedTouches[0].clientY - touchStartY;
                if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) goToIndex(dx < 0 ? currentIndex + 1 : currentIndex - 1);
              }, { passive: true });

              // Pinch-to-zoom
              let lastDist = null, lastScale = 1;
              lightboxImg.addEventListener('touchstart', function(e) { if (e.touches.length === 2) { lastDist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY); lastScale = currentScale; } }, { passive: true });
              lightboxImg.addEventListener('touchmove', function(e) { if (e.touches.length === 2 && lastDist) { const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY); setScale(lastScale * (dist / lastDist)); } }, { passive: true });
              lightboxImg.addEventListener('touchend', function() { lastDist = null; });

              // Escape key
              document.addEventListener('keydown', function(e) { if (e.key === 'Escape' && lightbox.classList.contains('is-open')) closeLightbox(); });

              // Build image list from non-duplicate slides
              function buildImageList() {
                const list = [];
                mainSwiper.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate) img').forEach(function(img) {
                  list.push({ src: img.src, alt: img.alt || '' });
                });
                return list;
              }

              // Use Swiper's click event to avoid conflicts with touch/drag handling
              swiperInstance.on('click', function() {
                imageList = buildImageList();
                const realIdx = swiperInstance.realIndex;
                currentIndex = Math.min(realIdx, imageList.length - 1);
                updateArrows();
                const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];
                const img = activeSlide ? activeSlide.querySelector('img') : null;
                if (img) openLightbox(img.src, img.alt);
              });

              // Set zoom-in cursor on all slides
              mainSwiper.querySelectorAll('.swiper-slide').forEach(function(slide) {
                slide.style.cursor = 'zoom-in';
              });
            }
          }
        });
      }
    };
    
    // Cache the instance
    componentInstances.set(sectionId, instance);
    console.log('MM Bundle: Created new instance for', sectionId);
    
    return instance;
  }

  // ========================================
  // Export for testing
  // ========================================

  if (typeof window !== 'undefined') {
    window.MMBundleLogic = {
      calculateTotalQuantity,
      getEffectiveMin,
      validateQuantity,
      getActiveTier,
      getUnlockedGifts,
      getCtaText,
      filterSellingPlans,
      getDefaultSellingPlan,
      generateBundleId,
      parseItemsFromUrl,
      serializeItemsToUrl
    };
  }

  // ========================================
  // Register with Alpine.js
  // ========================================

  function registerComponent() {
    // Use global flag to prevent duplicate registration across script reloads
    if (window._mmBundleState.registered) {
      console.log('MM Bundle: Component already registered globally, skipping');
      return;
    }
    if (typeof Alpine === 'undefined' || !Alpine.data) {
      return;
    }
    
    try {
      Alpine.data('mmBundle', mmBundle);
      window._mmBundleState.registered = true;
      console.log('MM Bundle: Component registered successfully');
    } catch (e) {
      // If component already exists, that's okay - mark as registered
      if (e.message && e.message.includes('already registered')) {
        window._mmBundleState.registered = true;
        console.log('MM Bundle: Component already registered');
      } else {
        console.error('MM Bundle: Failed to register component', e);
      }
    }
  }

  // ========================================
  // CRITICAL: Expose mmBundle as global function
  // ========================================
  // This ensures Alpine can find it when evaluating x-data expressions
  // even if Alpine.data() registration has timing issues
  window.mmBundle = mmBundle;
  
  // Expose registration function globally
  window.mmBundleRegister = registerComponent;

  // Register immediately if Alpine is already available
  if (typeof Alpine !== 'undefined' && Alpine.data) {
    registerComponent();
  }

  // Listen for alpine:init event (fires before Alpine scans DOM)
  document.addEventListener('alpine:init', () => {
    registerComponent();
  }, { once: true });

  // Fallback: wait for Alpine if it's not available yet
  if (typeof Alpine === 'undefined') {
    let attempts = 0;
    const maxAttempts = 100; // 5 seconds max wait
    const checkAlpine = () => {
      if (typeof Alpine !== 'undefined' && Alpine.data) {
        registerComponent();
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkAlpine, 50);
      }
    };
    checkAlpine();
  }

  // Handle Shopify customizer section reload
  document.addEventListener('shopify:section:load', function(event) {
    // Check if this is our section
    const bundleEl = event.target.querySelector('[x-data^="mmBundle"]');
    if (!bundleEl) return;
    
    console.log('MM Bundle: Section reloaded, reinitializing Alpine...');
    
    // Ensure component is registered
    registerComponent();
    
    // Initialize Alpine on the new DOM element
    if (typeof Alpine !== 'undefined' && Alpine.initTree) {
      // Small delay to ensure DOM is fully ready
      setTimeout(() => {
        Alpine.initTree(bundleEl);
      }, 10);
    }
  });

  // ========================================
  // Handle Barba.js page transitions
  // ========================================
  // This event is dispatched by page-transitions.ts after scripts are re-executed
  function reinitializeBundleComponents() {
    // Debounce: Prevent multiple rapid calls from destroying freshly initialized components
    const now = Date.now();
    const lastRun = window._mmBundleState.lastReinitTime || 0;
    if (now - lastRun < 200) {
      console.log('MM Bundle: Skipping reinitialization (debounced)');
      return;
    }
    window._mmBundleState.lastReinitTime = now;
    
    // FIRST: Clear stale initialization markers from cached HTML
    // These persist in Barba's page cache even after state is cleared
    document.querySelectorAll('[x-data^="mmBundle"][data-mm-initialized]').forEach(el => {
      el.removeAttribute('data-mm-initialized');
    });
    
    // Also clear the initialization tracking since we're reinitializing
    window._mmBundleState.initializedSections.clear();
    window._mmBundleState.componentInstances.clear();
    
    // Now find uninitialized bundle components
    const bundleEls = document.querySelectorAll('[x-data^="mmBundle"]');
    
    if (bundleEls.length === 0) return;
    
    console.log('MM Bundle: Reinitializing after Barba transition, found', bundleEls.length, 'components');
    
    // Ensure component is registered
    registerComponent();
    
    // Initialize Alpine on each bundle element
    bundleEls.forEach(bundleEl => {
      if (typeof Alpine !== 'undefined') {
        // CRITICAL: Destroy Alpine's internal state FIRST
        // Without this, Alpine reuses cached component and doesn't re-evaluate x-data
        if (Alpine.destroyTree) {
          Alpine.destroyTree(bundleEl);
        }
        // Now reinitialize with fresh state
        if (Alpine.initTree) {
          Alpine.initTree(bundleEl);
        }
      }
    });
  }

  // Listen for the custom event dispatched after Barba scripts are loaded
  document.addEventListener('barba:scripts:loaded', reinitializeBundleComponents);
  
  // Also listen for DOMContentLoaded (covers initial page load)
  document.addEventListener('DOMContentLoaded', function() {
    // Small delay to ensure scripts have executed
    setTimeout(reinitializeBundleComponents, 100);
  });
  
  // CRITICAL: Also run immediately when script loads
  // This handles Barba transitions where external scripts are deferred
  // and barba:scripts:loaded fires BEFORE this script has loaded
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    // Document already loaded, run immediately with small delay for Alpine
    setTimeout(reinitializeBundleComponents, 50);
  }
})();

